import React, { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement, 
  Title,
  Tooltip,
  Legend,
  BarController
} from "chart.js";
import { useTranslation } from "react-i18next";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement, 
  Title,
  Tooltip,
  Legend,
  BarController
);

interface MonthlyData {
  month: number;
  count_num_article: number;
  percent_article: number;
  count_num_content: number;
  percent_content: number;
  count_num_journal: number;
  percent_journal: number;
}

interface BarStatisticsChartProps {
  data: MonthlyData[];
  title: string;
  type: "count" | "percent";
  categories?: ("article" | "content" | "journal")[];
}

const BarStatisticsChart: React.FC<BarStatisticsChartProps> = ({
  data,
  title,
  type = "count",
  categories = ["article", "content", "journal"],
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const monthNames = [
    t("ม.ค."), t("ก.พ."), t("มี.ค."), t("เม.ย."), t("พ.ค."), t("มิ.ย."),
    t("ก.ค."), t("ส.ค."), t("ก.ย."), t("ต.ค."), t("พ.ย."), t("ธ.ค.")
  ];

  const categoryConfig = {
    article: {
      label: t("ข่าวประชาสัมพันธ์"),
      backgroundColor: "#FF6A6A",
      borderColor: "#E74C3C",
    },
    content: {
      label: t("ปฏิทินกิจกรรม"),
      backgroundColor: "#5FB95D",
      borderColor: "#26D0CE",
    },
    journal: {
      label: "Admission",
      backgroundColor: "#CB80E4",
      borderColor: "#7F8C8D",
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext("2d");

      if (ctx) {
        const datasets = categories.map((category) => ({
          label: categoryConfig[category].label,
          data: data.map((item) => {
            if (type === "count") {
              return item[`count_num_${category}` as keyof MonthlyData] as number;
            } else {
              return item[`percent_${category}` as keyof MonthlyData] as number;
            }
          }),
          backgroundColor: categoryConfig[category].backgroundColor,
          borderColor: categoryConfig[category].borderColor,
          borderWidth: 1,
        }));

        chartInstanceRef.current = new Chart(ctx, {
          type: "bar", // Keep as "bar" for this component
          data: {
            labels: data.map((item) => monthNames[item.month - 1]),
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: false,
              },
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    const value = context.parsed.y;
                    if (type === "count") {
                      return t("{{label}}: {{value}} รายการ", { label: context.dataset.label, value: value.toLocaleString() }) as string;
                    } else {
                      return `${context.dataset.label}: ${value.toFixed(2)}%`;
                    }
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                stacked: false,
                ticks: {
                  callback: function (value) {
                    if (type === "count") {
                      return (value as number).toLocaleString();
                    } else {
                      return `${value}%`;
                    }
                  },
                },
                grid: {
                  color: "#E5E5E5",
                },
              },
              x: {
                grid: {
                  display: false,
                },
              },
            },
          },
        });
      }
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [data, title, type, categories, t]);

  const legendItems = categories.map((category) => ({
    label: categoryConfig[category].label,
    color: categoryConfig[category].backgroundColor,
  }));

  return (
    <Card sx={{ width: "100%", maxWidth: "800px", maxHeight: "550px", minHeight: "550px" }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 2,
          }}
        >
          {title}
        </Typography>

        <Box sx={{ height: 410, minHeight: 410, maxHeight: 410 }}>
          <canvas ref={chartRef} />
        </Box>

        <Stack direction="row" justifyContent="center" spacing={4} mt={3}>
          {legendItems.map((item, index) => (
            <Stack direction="row" alignItems="center" key={index} spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: item.color,
                }}
              />
              <Typography variant="body2">{item.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BarStatisticsChart;